import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { ApiError } from './api';
import { showApiErrorToast } from './errorToast';

// Show an error toast by default for any failed query/mutation (restores the AngularJS
// global $http interceptor behavior). A query/mutation opts out by setting
// `meta: { suppressErrorToast: true }` — used when the component renders its own error UI —
// or only for some statuses with `meta: { suppressErrorStatuses: [400] }`.
const toastUnlessSuppressed = (error: unknown, meta?: Record<string, unknown>) => {
  if (meta?.suppressErrorToast === true) return;
  const statuses = meta?.suppressErrorStatuses as number[] | undefined;
  if (error instanceof ApiError && statuses?.includes(error.status)) return;
  showApiErrorToast(error);
};

// A 4xx comes back the same every time; retrying only delays the error. 401s are already
// refreshed and replayed inside api.ts.
const isClientError = (error: unknown) => error instanceof ApiError && error.status >= 400 && error.status < 500;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => toastUnlessSuppressed(error, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => toastUnlessSuppressed(error, mutation.options.meta),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => failureCount < 1 && !isClientError(error),
    },
  },
});
